<template>
  <ReportScheduledReportDialog
    v-if="show"
    show
    :custom-message-allowed="customMessageAllowed"
    :time-zone="timeZone"
    :time-zone-id="timeZoneId"
    :saving="reportSaving"
    :saving-preview="reportSavingPreview"
    :default-message="reportDefaultMessage"
    :default-subject="reportDefaultSubject"
    :message-maxlength="MESSAGE_MAX_LENGTH"
    :errors="reportErrors"
    :filter-count="filterCount"
    :filters-detailed="filtersDetailed"
    :report-name="REPORT_NAME"
    :delivery-frequency="DELIVERY_FREQUENCY"
    :scheduled-report="defaultScheduledReport"
    :scheduled-reports-improvements="false"
    :start-week-on="organization.start_week_on"
    :organization-id="organizationId"
    :cancel-button-label="CANCEL_BUTTON_LABEL"
    @save="onSaveReport"
    @preview="onPreviewReport"
    @close="onCloseReportDialog"
  />
</template>

<script>
import { ScheduledReportsResource, TeamsResource } from '~/resources';
import ReportScheduledReportDialog from '~/components/reports/ReportScheduledReportDialog.vue';
import {
  SCHEDULED_REPORT_INTERVAL_WEEKLY,
  SCHEDULED_REPORT_DATE_RANGE_LAST_WEEK,
  SCHEDULED_REPORT_FORMAT_PDF
} from '~/app_constants';

const REPORT_NAME = 'time_and_activities';
const DELIVERY_FREQUENCY = 'weekly';
const CANCEL_BUTTON_LABEL = "Don't schedule";
const MESSAGE_MAX_LENGTH = 400;
const DEFAULT_DELIVERY_TIME = '2000-01-01T08:30:00Z';
const MONDAY = 1;
const RECIPIENTS_LIMIT = 5;

export default {
  name: 'TeamScheduledReportDialog',
  components: {
    ReportScheduledReportDialog,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    team: {
      type: Object,
      default: null,
    },
    organization: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentUserEmail: null,
      reportSaving: false,
      reportSavingPreview: false,
      reportErrors: null,
      teamLeadEmails: [],
      REPORT_NAME,
      DELIVERY_FREQUENCY,
      CANCEL_BUTTON_LABEL,
      MESSAGE_MAX_LENGTH,
    };
  },
  computed: {
    organizationId() {
      return this.organization.id;
    },
    customMessageAllowed() {
      return this.organization.plan_paid;
    },
    timeZone() {
      return this.organization.time_zone_name;
    },
    timeZoneId() {
      return this.organization.time_zone_id;
    },
    teamName() {
      return this.team?.name || 'Team';
    },
    reportDefaultSubject() {
      return `${this.organization.name} ${this.teamName} – Time & Activity Report for __DATE_RANGE__`;
    },
    reportDefaultMessage() {
      return `${this.teamName}'s weekly report is attached. Please download the report or view in Hubstaff.`;
    },
    reportRecipients() {
      const recipients = new Set();

      if (this.currentUserEmail) {
        recipients.add(this.currentUserEmail);
      }

      this.teamLeadEmails.forEach(email => {
        if (email && recipients.size < RECIPIENTS_LIMIT) {
          recipients.add(email);
        }
      });

      return Array.from(recipients);
    },
    defaultScheduledReport() {
      return {
        recipients: this.reportRecipients,
        message: this.reportDefaultMessage,
        format: SCHEDULED_REPORT_FORMAT_PDF.toString(),
        name: `${this.teamName} Weekly`,
        date_range: SCHEDULED_REPORT_DATE_RANGE_LAST_WEEK.id,
        interval: SCHEDULED_REPORT_INTERVAL_WEEKLY,
        delivery_days: [MONDAY],
        delivery_time: DEFAULT_DELIVERY_TIME,
        force_next_sending_at: null,
      };
    },
    filterCount() {
      return this.team?.id ? 1 : 0;
    },
    filtersDetailed() {
      if (this.team?.id && this.team?.name) {
        return [
          {
            id: `team:${this.team.id}`,
            text: `Team: ${this.team.name}`
          }
        ];
      }

      return [];
    },
  },
  watch: {
    'team.id': {
      handler(newTeamId) {
        if (newTeamId) {
          this.fetchTeamLeadEmails();
        } else {
          this.teamLeadEmails = [];
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.currentUserEmail = JSON.parse(document.body.dataset.currentUserDetails || '{}').email;
  },
  methods: {
    async fetchTeamLeadEmails() {
      if (!this.team?.id) {
        this.teamLeadEmails = [];

        return;
      }

      try {
        const response = await TeamsResource.leadEmails({ id: this.team.id });

        this.teamLeadEmails = response.team_leads || [];
      } catch (error) {
        console.error('Failed to fetch team lead emails:', error);
        this.teamLeadEmails = [];
      }
    },
    buildSavedFilters() {
      const teamId = this.team?.id;

      return {
        report_custom_name: this.teamName,
        group_by: 'date',
        filters: {
          show_tasks: 'true',
          show_notes: 'true',
          show_activity: 'true',
          show_break_time: 'true',
          show_spent: 'true',
          show_spent_total: 'true',
          show_billable: '',
          include_archived: 'true',
          exclude_work_breaks: 'true',
          organization_id: this.organizationId,
          teams: teamId ? [`team:${teamId}`] : [],
          users: []
        }
      };
    },
    buildReportPayload(scheduledReport) {
      const savedReportFilter = {
        name: scheduledReport.name,
        report: REPORT_NAME,
        view: 'team',
        organization_id: this.organizationId,
        visible: false,
        saved_filters: this.buildSavedFilters()
      };

      return {
        track: {click_source: 'team_wizard'},
        organization_id: this.organizationId,
        scheduled_report: scheduledReport,
        saved_report_filter: savedReportFilter
      };
    },
    async onSaveReport(scheduledReport) {
      this.reportSaving = true;
      this.reportErrors = null;

      try {
        const reportPayload = this.buildReportPayload(scheduledReport);
        const response = await ScheduledReportsResource.create({}, reportPayload);

        if (response.success) {
          this.$emit('success', 'Team created and weekly report scheduled successfully!');
          this.onCloseReportDialog();
        } else {
          this.$appMessage('Unable to schedule the report');
          this.reportErrors = response.errors;
        }
      } catch (error) {
        console.error('Failed to save scheduled report:', error);
        this.$appMessage('Unable to schedule the report');
      } finally {
        this.reportSaving = false;
      }
    },
    async onPreviewReport(scheduledReport) {
      this.reportSavingPreview = true;

      try {
        const reportPayload = this.buildReportPayload(scheduledReport);
        const response = await ScheduledReportsResource.preview({}, reportPayload);

        if (response.success) {
          this.$appMessage(`Preview email sent to ${response.email}`);
        } else {
          this.$appMessage('Unable to send preview');
          this.reportErrors = response.errors;
        }
      } catch (error) {
        console.error('Failed to preview scheduled report:', error);
        this.$appMessage('Unable to send preview');
      } finally {
        this.reportSavingPreview = false;
      }
    },
    onCloseReportDialog() {
      this.$emit('close');
    },
  },
};
</script>
